// API Configuration - Centralized configuration to avoid circular dependencies
export const API_CONFIG = {
  BASE_URL:
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === "production"
      // ? "http://localhost:8000"
      // : "http://localhost:8000"),
// 
      ? "https://edu-nest.onrender.com"
      : "https://edu-nest.onrender.com"),


  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// Common API utilities
export const apiUtils = {
  // Build query string from object
  buildQueryString: (params) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          // Append each item individually with bracket notation to force array parsing on backend
          value.forEach(item => {
            if (item !== null && item !== undefined && item !== "") {
              searchParams.append(`${key}[]`, item);
            }
          });
        } else if (value !== "") {
          searchParams.append(key, value);
        }
      }
    });
    return searchParams.toString();
  },

  // Handle API response - parse structured errors where possible
  handleResponse: async (response) => {
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    if (!response.ok) {
      let errorData = {};
      if (isJson) {
        errorData = await response.json().catch(() => ({}));
      } else {
        const text = await response.text().catch(() => "");
        errorData = { message: text };
      }

      // Handle structured error response from backend
      // Format: { success: false, message: '...', error: 'ERROR_CODE', details: '...' }
      const validationErrors =
        errorData.errors || errorData.validationErrors || null;
      let message = errorData.message || "";

      // If backend sends validation errors array, join them
      if (
        (!message || message === "") &&
        validationErrors &&
        Array.isArray(validationErrors)
      ) {
        message = validationErrors
          .map((err) => err.msg || err.message || JSON.stringify(err))
          .join("; ");
      }

      // Fallback to error field if message is still empty
      if (!message && errorData.error) {
        message = errorData.error;
      }

      // Final fallback
      if (!message) {
        message = `HTTP error! status: ${response.status}`;
      }

      const err = new Error(message);
      // Attach structured error data for callers
      err.payload = errorData;
      err.success = errorData.success || false;
      err.error = errorData.error || null;
      err.details = errorData.details || null;
      err.status = response.status;
      throw err;
    }

    if (isJson) return response.json();
    const text = await response.text().catch(() => "");
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  },

  // Handle API error
  handleError: (error) => {
    throw error;
  },

  // Get auth headers
  getAuthHeaders: () => {
    const token = localStorage.getItem("authToken");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  },

  // Make authenticated request
  makeAuthenticatedRequest: async (url, options = {}) => {
    let headers = {
      ...apiUtils.getAuthHeaders(),
      ...options.headers,
    };

    // If DELETE and no body, remove Content-Type to avoid backend expecting a body
    if (options.method === 'DELETE' && (!options.body || options.body === undefined)) {
      // Remove Content-Type if present
      headers = Object.fromEntries(Object.entries(headers).filter(([k]) => k.toLowerCase() !== 'content-type'));
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });
      return await apiUtils.handleResponse(response);
    } catch (error) {
      // Re-throw with any available information (already enriched by handleResponse)
      apiUtils.handleError(error);
      throw error;
    }
  },
};

// API Error types
export const API_ERRORS = {
  NETWORK_ERROR: "NETWORK_ERROR",
  AUTHENTICATION_ERROR: "AUTHENTICATION_ERROR",
  AUTHORIZATION_ERROR: "AUTHORIZATION_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  NOT_FOUND_ERROR: "NOT_FOUND_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
};

// API Response status codes
export const API_STATUS = {
  SUCCESS: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
};
